/**
 * Trunnion AI - LLM Provider Configuration
 * Provider management interface.
 */

import { useState } from 'react';
import { Plus, Cpu, Trash2, Edit2, Zap } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import type { LLMProvider } from '@/types';

// Using local state; in production wired to useLLMProviders
export function LLMProviderConfig() {
  const [providers] = useState<LLMProvider[]>([]);
  const [isLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  if (isLoading) return <LoadingSpinner size="lg" label="Loading providers..." />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">LLM Providers</h2>
        <Button
          icon={<Plus className="h-4 w-4" />}
          onClick={() => setCreateOpen(true)}
        >
          Add Provider
        </Button>
      </div>

      {providers.length === 0 ? (
        <Card className="text-center py-8">
          <Cpu className="h-8 w-8 mx-auto text-trunnion-slate-500 mb-2" />
          <p className="text-sm text-trunnion-slate-400">No LLM providers configured</p>
          <Button
            className="mt-4"
            size="sm"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => setCreateOpen(true)}
          >
            Add Provider
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {providers.map((provider) => (
            <Card key={provider.id}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-trunnion-gold-500/10">
                    <Cpu className="h-5 w-5 text-trunnion-gold-500" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-white">{provider.name}</h3>
                    <p className="text-xs text-trunnion-slate-400 capitalize">{provider.type}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Badge variant={provider.isActive ? 'success' : 'default'} dot>
                    {provider.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
              <div className="mt-3 text-xs text-trunnion-slate-400">
                <p>{provider.models.length} models available</p>
                <p>Rate: {provider.rateLimits.requestsPerMinute} req/min</p>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Button variant="ghost" size="sm" icon={<Zap className="h-3.5 w-3.5" />}>
                  Test
                </Button>
                <Button variant="ghost" size="sm" icon={<Edit2 className="h-3.5 w-3.5" />}>
                  Edit
                </Button>
                <Button variant="ghost" size="sm" icon={<Trash2 className="h-3.5 w-3.5 text-red-400" />} />
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Add LLM Provider"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => setCreateOpen(false)}>Add Provider</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input label="Provider Name" placeholder="e.g., OpenAI Production" />
          <Select
            label="Provider Type"
            options={[
              { value: 'openai', label: 'OpenAI' },
              { value: 'anthropic', label: 'Anthropic' },
              { value: 'azure_openai', label: 'Azure OpenAI' },
              { value: 'bedrock', label: 'AWS Bedrock' },
              { value: 'local', label: 'Local' },
              { value: 'custom', label: 'Custom' },
            ]}
          />
          <Input label="Base URL" placeholder="https://api.openai.com/v1" />
          <Input label="API Key" type="password" placeholder="sk-..." />
        </div>
      </Modal>
    </div>
  );
}
